import { Add, Delete, Done } from '@mui/icons-material';
import { Checkbox } from '@mui/material';
import { generateFullName } from '@sharedInterfaces/globalHelper';
import type { ICompanyUser } from '@sharedInterfaces/ICompanySettings';
import { EGender } from '@sharedInterfaces/IEmployee';
import { defaultEntitiesPermissions, type IPermissionGroup } from '@sharedInterfaces/IPermissions';
import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import Button from '@src/App/Components/Buttons/Button/Button';
import Dialog from '@src/App/Components/Dialogs/Dialog/Dialog';
import GenderSelect from '@src/App/Components/General/FormularElements/formsControls/inputs/GenderSelect/GenderSelect';
import PermissionGroupSelect from '@src/App/Components/General/FormularElements/formsControls/inputs/PermissionGroupSelect/PermissionGroupSelect';
import TextInput from '@src/App/Components/General/FormularElements/formsControls/inputs/TextInput/TextInput';
import type { OU } from '@src/Objects/Company';
import type { AppState } from '@store/store';
import React from 'react';
import { useSelector } from 'react-redux';
import ErrorBox from '../../Error/ErrorBox/ErrorBox';
import RowElement from '../../General/FormularElements/RowElement/RowElement';

interface AddEmployeesDialogProps {
    id: string;
    ou: OU;
    permissionGroups: IPermissionGroup[];
    resolve: () => void;
    onSave: (val: ICompanyUser[]) => Promise<unknown>;
}
/**
 * Represents a dialog for adding employees.
 *
 * @param {AddEmployeesDialogProps} props - The properties for the AddEmployeesDialog.
 *
 * @returns {JSX.Element} - The JSX element representing the dialog for adding employees.
 */
export function AddEmployeesDialog(props: AddEmployeesDialogProps) {
    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang).userDialog;
    const [employees, setEmployees] = React.useState([] as ICompanyUser[]);
    const [saving, setSaving] = React.useState(false as boolean);
    const defaultNewEmp: ICompanyUser = {
        firstname: '',
        lastname: '',
        email: '',
        gender: EGender.OTHER,
        permissionGroup: {
            id: -1,
            title: '',
        },
        permissions: {
            admin: false,
            entities: defaultEntitiesPermissions,
        },
    } as ICompanyUser;
    const [newEmployee, setNewEmployee] = React.useState(defaultNewEmp);
    const [error, setError] = React.useState('');

    return (
        <Dialog
            id={props.id}
            title={langStrings.inviteEmployeesTo + props.ou.title}
            footer={
                <div style={{ float: 'right' }}>
                    <Button
                        icon={<Done />}
                        disabled={saving || employees.length === 0}
                        text={langStrings.invite}
                        size={'normal'}
                        onClick={async (): Promise<void> => {
                            if (employees.length === 0) {
                                return props.resolve();
                            }
                            setSaving(true);
                            await props.onSave(employees).catch((ex) => {
                                setError(ex.toString());
                                setTimeout(() => {
                                    setSaving(false);
                                }, 3000);
                            });
                        }}
                    />
                </div>
            }
            onClose={(): void => {
                props.resolve();
            }}
        >
            <div className="addUserDialog">
                <h3>{langStrings.addDescription}</h3>

                <div className="unsavedUsers">
                    {employees.map((e) => (
                        <div className="unsavedUser" key={e.email}>
                            <div className="headLine">
                                <div className="emailNewEmployee">
                                    <RowElement title={langStrings.name} alignTitle="left">
                                        <div className="employeeName">{generateFullName(e.firstname, e.lastname)}</div>
                                    </RowElement>
                                </div>
                                <div
                                    className="removeUser"
                                    onClick={() => {
                                        setEmployees(employees.filter((eo) => eo.email !== e.email));
                                    }}
                                >
                                    <Delete />
                                </div>
                            </div>
                            <RowElement title="E-Mail" alignTitle="left">
                                {e.email}
                            </RowElement>
                        </div>
                    ))}
                </div>

                <div className="newUserRow">
                    <RowElement title={langStrings.firstname} alignTitle="left">
                        <TextInput
                            value={newEmployee.firstname}
                            size="small"
                            onChange={(value) => {
                                setNewEmployee({
                                    ...newEmployee,
                                    firstname: value,
                                });
                            }}
                        />
                    </RowElement>
                    <RowElement title={langStrings.lastname} alignTitle="left">
                        <TextInput
                            value={newEmployee.lastname}
                            size="small"
                            onChange={(value) => {
                                setNewEmployee({
                                    ...newEmployee,
                                    lastname: value,
                                });
                            }}
                        />
                    </RowElement>
                    <RowElement title="E-Mail" alignTitle="left">
                        <TextInput
                            value={newEmployee.email}
                            size="small"
                            onChange={(value) => {
                                setNewEmployee({
                                    ...newEmployee,
                                    email: value,
                                });
                            }}
                        />
                    </RowElement>
                    <RowElement title={langStrings.gender} alignTitle="left">
                        <GenderSelect
                            value={newEmployee.gender}
                            onChange={(value) =>
                                setNewEmployee({
                                    ...newEmployee,
                                    gender: value,
                                })
                            }
                        />
                    </RowElement>
                    <RowElement title={langStrings.permissionGroup} alignTitle="left">
                        <PermissionGroupSelect
                            permissionGroups={props.permissionGroups}
                            selectedPermissionGroup={newEmployee.permissionGroup ? newEmployee.permissionGroup.id : -1}
                            setSelectedPermissionGroup={(pg) =>
                                setNewEmployee({
                                    ...newEmployee,
                                    permissionGroup: { id: pg.id, title: pg.title },
                                    permissions: { ...newEmployee.permissions, entities: pg.entities },
                                })
                            }
                        />
                    </RowElement>
                    <RowElement title={langStrings.admin} alignTitle="left">
                        <Checkbox
                            checked={newEmployee.permissions.admin}
                            onChange={(e) =>
                                setNewEmployee({
                                    ...newEmployee,
                                    permissions: { ...newEmployee.permissions, admin: e.target.checked },
                                })
                            }
                        />
                    </RowElement>
                    {error !== '' && <ErrorBox>{error}</ErrorBox>}

                    <Button
                        size="verysmall"
                        disabled={false}
                        onClick={() => {
                            if (newEmployee.firstname === '') {
                                setError(langStrings.pleaseEnterFirstName);
                                return;
                            } else if (newEmployee.lastname === '') {
                                setError(langStrings.pleaseEnterLastName);
                                return;
                            } else if (
                                newEmployee.email.indexOf('@') === -1 ||
                                newEmployee.email.lastIndexOf('.') < newEmployee.email.lastIndexOf('@')
                            ) {
                                setError(langStrings.pleaseEnterVaildEmail);
                                return;
                            } else if (employees.map((e) => e.email).includes(newEmployee.email)) {
                                setError(langStrings.canNotAddEmailTwice);
                                return;
                            } else if (newEmployee.permissionGroup.id === -1) {
                                setError(langStrings.pleaseSelectPermissionGroup);
                                return;
                            } else {
                                const newEmployees = employees.slice();
                                newEmployees.push(newEmployee);
                                setEmployees(newEmployees);
                                setNewEmployee(defaultNewEmp);
                                if (error) setError('');
                            }
                        }}
                        icon={<Add />}
                        text={langStrings.invite}
                    />
                </div>
            </div>
        </Dialog>
    );
}
